import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Star, Trophy, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import Button from '../components/common/Button';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';

export default function ProfileSelection() {
  const navigate = useNavigate();
  const { profiles, selectProfile, deleteProfile, currentProfile } = useGameStore();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<typeof profiles[0] | null>(null);

  const handleSelectProfile = async (profileId: string) => {
    await selectProfile(profileId);
    navigate('/levels');
  };

  const handleDeleteClick = (e: React.MouseEvent, profile: typeof profiles[0]) => {
    e.stopPropagation();
    setProfileToDelete(profile);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (profileToDelete) {
      await deleteProfile(profileToDelete.id);
      setDeleteModalOpen(false);
      setProfileToDelete(null);
    }
  };

  return (
    <div className="min-h-screen p-4 relative">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-4xl font-bold text-white">Who's Playing?</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {profiles.map((profile, index) => (
            <motion.div
              key={profile.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                className={`relative p-6 cursor-pointer transition-all duration-200 hover:scale-105 ${
                  currentProfile?.id === profile.id 
                    ? 'ring-2 ring-purple-500 bg-purple-900/20' 
                    : 'bg-white/10 backdrop-blur'
                } border-white/20 group`}
                onClick={() => handleSelectProfile(profile.id)}
              >
                <button
                  onClick={(e) => handleDeleteClick(e, profile)}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>

                <div className="flex flex-col items-center">
                  <div 
                    className="w-20 h-20 rounded-full mb-4 flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: profile.color }}
                  >
                    <img 
                      src={profile.avatar} 
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {profile.name}
                  </h3>
                  
                  <div className="flex flex-col gap-2 text-sm text-gray-300 w-full">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400" />
                        Stars
                      </span>
                      <span>{profile.totalStars || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-purple-400" />
                        Levels
                      </span>
                      <span>{profile.levelsCompleted || 0}/10</span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: profiles.length * 0.1 }}
          >
            <Card
              className="p-6 cursor-pointer transition-all duration-200 hover:scale-105 bg-white/10 backdrop-blur border-white/20 border-dashed border-2 min-h-[250px] flex items-center justify-center"
              onClick={() => navigate('/profile/new')}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-purple-600/20 flex items-center justify-center">
                  <Plus className="w-10 h-10 text-purple-400" />
                </div>
                <span className="text-lg font-semibold text-purple-400">Add Player</span>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProfileToDelete(null);
        }}
        size="sm"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="mx-auto mb-4 w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center"
          >
            <AlertTriangle className="w-10 h-10 text-red-400" />
          </motion.div>
          
          <h3 className="text-2xl font-bold text-white mb-2">
            Delete Profile?
          </h3>
          
          {profileToDelete && (
            <div className="mb-4">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div 
                  className="w-12 h-12 rounded-full overflow-hidden"
                  style={{ backgroundColor: profileToDelete.color }}
                >
                  <img 
                    src={profileToDelete.avatar} 
                    alt={profileToDelete.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xl text-white font-semibold">
                  {profileToDelete.name}
                </span>
              </div>
              
              <div className="space-y-2 text-gray-400">
                <p>This will permanently delete:</p>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    {profileToDelete.totalStars || 0} stars earned
                  </li>
                  <li className="flex items-center justify-center gap-2">
                    <Trophy className="w-4 h-4 text-purple-400" />
                    {profileToDelete.levelsCompleted || 0} levels completed
                  </li>
                </ul>
              </div>
            </div>
          )}
          
          <p className="text-red-400 mb-6 font-semibold">
            ⚠️ This action cannot be undone!
          </p>
          
          <div className="flex gap-3">
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setDeleteModalOpen(false);
                setProfileToDelete(null);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={handleConfirmDelete}
              className="flex-1"
            >
              Delete Profile
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}